import {
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Label,
  Text,
  type OptionOnSelectData,
  type SelectionEvents,
} from '@fluentui/react-components';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  dropdown: {
    width: '100%',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface DropdownOption {
  label: string;
  value: string;
  description?: string;
}

export interface DropdownFieldProps {
  name: string;
  label: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
}

/**
 * DropdownField - Select dropdown with options
 */
export const DropdownField = ({
  name,
  label,
  value,
  options,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
}: DropdownFieldProps) => {
  const styles = useStyles();

  const selectedOption = options.find(opt => opt.value === value);

  const handleChange = (_: SelectionEvents, data: OptionOnSelectData) => {
    if (data.optionValue) {
      onChange(data.optionValue);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
      </div>

      <Dropdown
        id={name}
        className={styles.dropdown}
        value={selectedOption?.label || ''}
        selectedOptions={value ? [value] : []}
        onOptionSelect={handleChange}
        placeholder={placeholder || description || 'Select an option...'}
        disabled={disabled}
        aria-invalid={!!error}
      >
        {options.map((option) => (
          <Option key={option.value} value={option.value} text={option.label}>
            {option.label}
          </Option>
        ))}
      </Dropdown>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

DropdownField.displayName = 'DropdownField';
